import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { offerApi } from '../../lib/offerApi';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, RefreshCw, FileText, CheckCircle2, Clock,
  User, Briefcase, DollarSign, Calendar, Copy, ExternalLink, Download
} from 'lucide-react';

const STATUS_BADGE = {
  draft: 'bg-stone-100 text-stone-600',
  sent: 'bg-blue-50 text-blue-600',
  selecting: 'bg-amber-50 text-amber-600',
  awaiting_acceptance: 'bg-purple-50 text-purple-600',
  accepted: 'bg-emerald-50 text-emerald-600',
  declined: 'bg-red-50 text-red-600',
  expired: 'bg-stone-100 text-stone-400',
};

const EVENT_LABELS = {
  created: 'Offer created',
  sent: 'Email sent to candidate',
  opened: 'Candidate opened link',
  selections_started: 'Candidate started selecting benefits',
  selections_completed: 'Candidate completed benefit selections',
  accepted: 'Candidate accepted and signed',
  declined: 'Candidate declined',
  expired: 'Offer expired',
  resent: 'Offer resent to candidate',
  converted: 'Converted to employee',
};

const CATEGORY_ICONS = {
  food: { icon: '🍽', label: 'Food & Meals' },
  transport: { icon: '🚗', label: 'Transport' },
  health: { icon: '🏥', label: 'Health & Wellness' },
  insurance: { icon: '🛡', label: 'Insurance' },
  housing: { icon: '🏠', label: 'Housing' },
  education: { icon: '📚', label: 'Education' },
  devices: { icon: '💻', label: 'Devices' },
  lifestyle: { icon: '🎉', label: 'Lifestyle' },
};

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function OfferLetterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOffer(); }, [id]);

  const loadOffer = async () => {
    try {
      const data = await offerApi.detail(id);
      setOffer(data);
    } catch (err) {
      toast.error('Failed to load offer');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      await offerApi.send(id);
      toast.success('Offer sent to candidate!');
      loadOffer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send offer');
    }
  };

  const handleResend = async () => {
    try {
      await offerApi.resend(id);
      toast.success('Offer resent');
      loadOffer();
    } catch (err) {
      toast.error('Failed to resend');
    }
  };

  const copyLink = () => {
    if (!offer) return;
    const url = `${window.location.origin}/offer/${offer.unique_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Offer link copied to clipboard');
  };

  const handleDownloadPdf = async () => {
    try {
      const resp = await offerApi.downloadPdf(id);
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Offer_Letter_${offer.candidate_name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-3 border-pink-300 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!offer) return (
    <div className="text-center py-20">
      <p className="text-[#636E72]">Offer not found</p>
    </div>
  );

  const tax = offer.tax_preview || {};
  const totalPkg = offer.gross_monthly + offer.benefits_budget;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="offer-detail-page">
      {/* Back + Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/company-admin/offer-letters')} className="p-2 rounded-lg hover:bg-stone-100 transition-colors" data-testid="back-to-offers">
            <ArrowLeft className="w-5 h-5 text-[#636E72]" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#2D3436] font-[Manrope]">{offer.candidate_name} — {offer.position_title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[offer.status] || STATUS_BADGE.draft}`}>
                {offer.status?.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}
              </span>
              <span className="text-xs text-[#636E72]">Created {new Date(offer.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {offer.status === 'draft' && (
            <button onClick={handleSend} className="flex items-center gap-2 px-4 py-2 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-xl text-sm font-semibold shadow-sm transition-all active:scale-95" data-testid="send-offer-btn">
              <Send className="w-4 h-4" /> Send Offer
            </button>
          )}
          {['sent', 'selecting', 'awaiting_acceptance'].includes(offer.status) && (
            <>
              <button onClick={copyLink} className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-xl text-sm font-medium hover:bg-stone-50 transition-colors" data-testid="copy-link-btn">
                <Copy className="w-4 h-4" /> Copy Link
              </button>
              <button onClick={handleResend} className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-xl text-sm font-medium hover:bg-stone-50 transition-colors" data-testid="resend-btn">
                <RefreshCw className="w-4 h-4" /> Resend
              </button>
            </>
          )}
          {offer.status !== 'draft' && (
            <a href={`/offer/${offer.unique_token}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-xl text-sm font-medium hover:bg-stone-50 transition-colors">
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
          )}
          <button onClick={handleDownloadPdf} className="flex items-center gap-2 px-4 py-2 bg-[#2D3436] hover:bg-[#1a1d1f] text-white rounded-xl text-sm font-semibold shadow-sm transition-all active:scale-95" data-testid="download-pdf-btn">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Package Summary */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-[#636E72] uppercase tracking-wider mb-4">Package Summary</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-[#636E72]">Gross Salary</p>
                <p className="text-lg font-bold text-[#2D3436] font-[Manrope]">{fmt(offer.gross_monthly)}</p>
              </div>
              <div>
                <p className="text-xs text-[#636E72]">Benefits Budget</p>
                <p className="text-lg font-bold text-[#D64D7A] font-[Manrope]">{fmt(offer.benefits_budget)}</p>
              </div>
              <div>
                <p className="text-xs text-[#636E72]">Total Package</p>
                <p className="text-lg font-bold text-[#2D3436] font-[Manrope]">{fmt(totalPkg)}</p>
              </div>
            </div>
            {tax.monthly_paye !== undefined && (
              <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-[#636E72]">Monthly PAYE</p>
                  <p className="font-semibold text-[#2D3436]">{fmt(tax.monthly_paye)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#636E72]">Take-home Cash</p>
                  <p className="font-semibold text-[#2D3436]">{fmt(tax.monthly_take_home)}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Tax Saved/mo</p>
                  <p className="font-semibold text-emerald-600">{fmt(tax.tax_saved_monthly)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Candidate Selections */}
          {offer.candidate_selections && (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
              <h2 className="text-sm font-semibold text-[#636E72] uppercase tracking-wider mb-4">Selected Benefits</h2>
              <div className="space-y-3">
                {Object.entries(offer.candidate_selections).map(([cat, amount]) => {
                  const ci = CATEGORY_ICONS[cat] || { icon: '📦', label: cat };
                  const pct = offer.benefits_budget > 0 ? Math.round(amount / offer.benefits_budget * 100) : 0;
                  return (
                    <div key={cat} className="flex items-center gap-3">
                      <span className="text-lg">{ci.icon}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-[#2D3436]">{ci.label}</span>
                          <span className="font-semibold text-[#2D3436]">{fmt(amount)}</span>
                        </div>
                        <div className="w-full h-1.5 bg-stone-100 rounded-full mt-1">
                          <div className="h-full bg-[#D64D7A] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Candidate Details */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-[#636E72] uppercase tracking-wider mb-4">Candidate Details</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-[#636E72]">Email</p><p className="font-medium">{offer.candidate_email}</p></div>
              <div><p className="text-[#636E72]">Phone</p><p className="font-medium">{offer.candidate_phone || '-'}</p></div>
              <div><p className="text-[#636E72]">Department</p><p className="font-medium">{offer.department || '-'}</p></div>
              <div><p className="text-[#636E72]">Reporting To</p><p className="font-medium">{offer.reporting_to || '-'}</p></div>
              <div><p className="text-[#636E72]">Start Date</p><p className="font-medium">{offer.start_date}</p></div>
              <div><p className="text-[#636E72]">Employment Type</p><p className="font-medium capitalize">{offer.employment_type?.replace('_', ' ')}</p></div>
              <div><p className="text-[#636E72]">Probation</p><p className="font-medium">{offer.probation_months} months</p></div>
              <div><p className="text-[#636E72]">Work Arrangement</p><p className="font-medium capitalize">{offer.work_arrangement}</p></div>
              <div><p className="text-[#636E72]">Offer Expiry</p><p className="font-medium">{offer.offer_expiry_date}</p></div>
              <div><p className="text-[#636E72]">Selection Mode</p><p className="font-medium capitalize">{offer.selection_mode?.replace('_', ' ')}</p></div>
            </div>
          </div>
        </div>

        {/* Sidebar - Timeline */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-[#636E72] uppercase tracking-wider mb-4">Timeline</h2>
            <div className="space-y-4">
              {(offer.events || []).map((ev, i) => (
                <div key={ev.id} className="flex gap-3" data-testid={`event-${ev.event_type}`}>
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#D64D7A] mt-1" />
                    {i < (offer.events.length - 1) && <div className="w-px flex-1 bg-stone-200 mt-1" />}
                  </div>
                  <div className="pb-4">
                    <p className="text-sm font-medium text-[#2D3436]">{EVENT_LABELS[ev.event_type] || ev.event_type}</p>
                    <p className="text-xs text-[#636E72]">{ev.created_at ? new Date(ev.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Info */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm space-y-3 text-sm">
            <h2 className="text-sm font-semibold text-[#636E72] uppercase tracking-wider mb-2">Salary Breakdown</h2>
            {tax.salary_breakdown && Object.entries(tax.salary_breakdown).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-[#636E72] capitalize">{k} ({offer.salary_structure?.[`${k}_pct`]}%)</span>
                <span className="font-medium">{fmt(v)}</span>
              </div>
            ))}
          </div>

          {offer.signed_name && (
            <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-2" />
              <p className="font-semibold text-emerald-700">Signed by {offer.signed_name}</p>
              <p className="text-xs text-emerald-600 mt-1">{offer.signed_at ? new Date(offer.signed_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
